import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome back, ${u.name}`);
      const dash =
        u.role === "admin" ? "/dashboard/agency"
        : u.role === "employer" ? "/dashboard/employer"
        : "/dashboard/candidate";
      nav(dash);
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] container-ax py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
      <div>
        <div className="eyebrow mb-2">Welcome back</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-navy">Sign in to Recruvo.</h1>
        <p className="mt-4 text-slate-600 max-w-md">Access your candidate profile, employer pipeline, or agency dashboard.</p>
      </div>
      <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-8 bg-white space-y-4 shadow-sm">
        <GoogleSignInButton label="Sign in with Google" />
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-slate-200" />
          <div className="text-[11px] uppercase tracking-widest text-slate-400">or with email</div>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Email</label>
          <input required type="email" value={email} onChange={e => setEmail(e.target.value)}
            data-testid={TID.loginEmail}
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Password</label>
          <input required type="password" value={password} onChange={e => setPassword(e.target.value)}
            data-testid={TID.loginPassword}
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <button disabled={busy} type="submit" data-testid={TID.loginSubmit}
          className="btn-navy w-full justify-center disabled:opacity-60">
          <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
        </button>
        <div className="text-sm text-slate-500 text-center">
          New to Recruvo? <Link to="/register" className="text-navy font-medium hover:text-gold">Create an account</Link>
        </div>
      </form>
    </div>
  );
}
